import React, { useState, useEffect } from 'react';
import {
	FileText,
	Upload,
	Trash2,
	X,
	AlertCircle,
	Database,
	Plus,
	RefreshCw,
	Check,
	Clock,
	XCircle,
	Info
} from 'lucide-react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogPortal,
	DialogOverlay,
} from '../../../components/ui/dialog';
import { toast } from 'react-toastify';
import { channels } from '../../../shared/constants';

const { ipcRenderer } = window.require('electron');

const formatFileSize = (bytes) => {
	if (bytes === 0) return '0 Bytes';
	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const formatDate = (isoString) => {
	if (!isoString) return 'N/A';
	const date = new Date(isoString);
	return date.toLocaleDateString('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit'
	});
};

const getFileIcon = (mimeType) => {
	if (!mimeType) return <FileText className="w-5 h-5" />;

	if (mimeType.includes('pdf')) return <FileText className="w-5 h-5 text-red-400" />;
	if (mimeType.includes('word')) return <FileText className="w-5 h-5 text-blue-400" />;
	if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return <FileText className="w-5 h-5 text-green-400" />;
	if (mimeType.includes('text')) return <FileText className="w-5 h-5 text-gray-400" />;

	return <FileText className="w-5 h-5" />;
};

const getStateColor = (state) => {
	switch (state) {
		case 'ACTIVE':
			return 'text-green-400';
		case 'PENDING':
			return 'text-yellow-400';
		case 'FAILED':
			return 'text-red-400';
		default:
			return 'text-gray-400';
	}
};

const getStateIcon = (state) => {
	switch (state) {
		case 'ACTIVE':
			return <Check className="w-4 h-4" />;
		case 'PENDING':
			return <Clock className="w-4 h-4" />;
		case 'FAILED':
			return <XCircle className="w-4 h-4" />;
		default:
			return null;
	}
};

const SUPPORTED_EXTENSIONS = ['.pdf', '.docx', '.xlsx', '.txt', '.md', '.json', '.csv'];
const MAX_FILE_SIZE_MB = 100;
const MAX_STORES = 10;

const KnowledgeBaseSection = ({ isOpen, onClose, instanceId, onChange, aiProvider = 'GEMINI' }) => {
	const [stores, setStores] = useState([]);
	const [selectedStore, setSelectedStore] = useState(null);
	const [documents, setDocuments] = useState([]);
	const [activeStoreName, setActiveStoreName] = useState(null);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [showCreateDialog, setShowCreateDialog] = useState(false);
	const [newStoreName, setNewStoreName] = useState('');
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [storeToDelete, setStoreToDelete] = useState(null);
	const [deleteConfirmName, setDeleteConfirmName] = useState('');

	const isGemini = aiProvider === 'GEMINI';
	const isGPT = aiProvider === 'GPT';

	useEffect(() => {
		if (isOpen && instanceId) {
			setSelectedStore(null);
			setDocuments([]);
			loadStores();
			loadActiveStore();
		}
	}, [isOpen, instanceId, aiProvider]);

	useEffect(() => {
		if (activeStoreName && stores.length > 0 && !selectedStore) {
			const activeStore = stores.find(store => store.name === activeStoreName);
			if (activeStore) {
				setSelectedStore(activeStore);
				loadDocuments(activeStore.name);
			}
		}
	}, [activeStoreName, stores]);

	const loadStores = async () => {
		setLoading(true);
		try {
			const channel = isGemini ? channels.LIST_GEMINI_STORES : channels.LIST_OPENAI_STORES;
			const result = await ipcRenderer.invoke(channel, instanceId);

			if (result.success) {
				const normalizedStores = (result.stores || []).map(store => ({
					name: isGemini ? store.name : store.id,
					id: isGemini ? store.name : store.id,
					displayName: store.displayName || store.name || store.id,
					documentCount: isGemini ? (store.documentCount || 0) : (store.fileCount || 0)
				}));
				setStores(normalizedStores);
			} else {
				console.error('Error loading stores:', result.error);
				toast.error(`Erro ao carregar stores: ${result.error}`);
			}
		} catch (error) {
			console.error('Error loading stores:', error);
			toast.error('Erro ao carregar stores');
		} finally {
			setLoading(false);
		}
	};

	const loadActiveStore = async () => {
		try {
			const channel = isGemini ? channels.GET_ACTIVE_STORE : channels.GET_ACTIVE_OPENAI_STORE;
			const result = await ipcRenderer.invoke(channel, instanceId);

			if (result.success) {
				setActiveStoreName(isGemini ? result.storeName : result.vectorStoreId);
			}
		} catch (error) {
			console.error('Error loading active store:', error);
		}
	};

	const loadDocuments = async (storeName) => {
		setLoading(true);
		try {
			const channel = isGemini ? channels.LIST_STORE_DOCUMENTS : channels.LIST_OPENAI_STORE_FILES;
			const params = isGemini
				? { instanceId, storeName }
				: { instanceId, vectorStoreId: storeName };

			const result = await ipcRenderer.invoke(channel, params);

			if (result.success) {
				const normalizedDocuments = isGemini
					? (result.documents || [])
					: (result.files || []).map(file => ({
							name: file.fileId,
							displayName: file.displayName || file.fileName,
							sizeBytes: file.size,
							createTime: file.uploadedAt,
							state: file.status === 'completed' ? 'ACTIVE' : 'PENDING',
							mimeType: 'application/octet-stream'
					}));
				setDocuments(normalizedDocuments);
				
				setStores(prevStores => prevStores.map(store => 
					(store.name === storeName || store.id === storeName)
						? { ...store, documentCount: normalizedDocuments.length }
						: store
				));
			} else {
				console.error('Error loading documents:', result.error);
				toast.error(`Erro ao carregar arquivos: ${result.error}`);
			}
		} catch (error) {
			console.error('Error loading documents:', error);
			toast.error('Erro ao carregar arquivos');
		} finally {
			setLoading(false);
		}
	};

	const handleSelectStore = async (store) => {
		setSelectedStore(store);
		loadDocuments(store.name);

		try {
			const channel = isGemini ? channels.SET_ACTIVE_STORE : channels.SET_ACTIVE_OPENAI_STORE;
			const params = isGemini
				? { instanceId, storeName: store.name }
				: { instanceId, vectorStoreId: store.id };

			const result = await ipcRenderer.invoke(channel, params);

			if (result.success) {
				if(activeStoreName == store.name){
					setSelectedStore(null);
					setDocuments([]);
					setActiveStoreName(null);
					toast.info(`Store "${store.displayName}" desativado da IA!`);
					if (onChange) onChange();
					return;
				}
				setActiveStoreName(store.name);
				toast.success(`Store "${store.displayName}" ativado para IA!`);
				if (onChange) onChange();
				
			} else {
				toast.error(`Erro ao ativar store: ${result.error}`);
			}
		} catch (error) {
			console.error('Error activating store:', error);
			toast.error('Erro ao ativar store');
		}
	};

	const handleCreateStore = async () => {
		if (!newStoreName || newStoreName.trim().length === 0) {
			toast.error('Digite um nome para o store');
			return;
		}

		setLoading(true);
		try {
			const channel = isGemini ? channels.CREATE_GEMINI_STORE : channels.CREATE_OPENAI_STORE;
			const result = await ipcRenderer.invoke(channel, {
				instanceId,
				displayName: newStoreName.trim()
			});

			if (result.success) {
				toast.success('Store criado com sucesso!');
				setNewStoreName('');
				setShowCreateDialog(false);
				await loadStores();
			} else {
				toast.error(`Erro ao criar store: ${result.error}`);
			}
		} catch (error) {
			console.error('Error creating store:', error);
			toast.error('Erro ao criar store');
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteStoreClick = (store) => {
		setStoreToDelete(store);
		setDeleteConfirmName('');
		setShowDeleteConfirm(true);
	};

	const handleConfirmDelete = async () => {
		if (!storeToDelete) return;

		if (deleteConfirmName !== storeToDelete.displayName) {
			toast.error('Nome incorreto! Digite exatamente como mostrado.');
			return;
		}

		setLoading(true);
		try {
			const channel = isGemini ? channels.DELETE_GEMINI_STORE : channels.DELETE_OPENAI_SPECIFIC_STORE;
			const params = isGemini
				? { instanceId, storeName: storeToDelete.name }
				: { instanceId, vectorStoreId: storeToDelete.id };

			const result = await ipcRenderer.invoke(channel, params);

			if (result.success) {
				toast.success('Store deletado com sucesso!');
				setShowDeleteConfirm(false);
				setStoreToDelete(null);
				setDeleteConfirmName('');

				if (selectedStore?.name === storeToDelete.name) {
					setSelectedStore(null);
					setDocuments([]);
				}

				await loadStores();
				await loadActiveStore();

				if (onChange) {
					onChange();
				}
			} else {
				toast.error(`Erro ao deletar store: ${result.error}`);
			}
		} catch (error) {
			console.error('Error deleting store:', error);
			toast.error('Erro ao deletar store');
		} finally {
			setLoading(false);
		}
	};

	const handleUploadFile = async () => {
		if (!selectedStore) {
			toast.error('Selecione um store primeiro!');
			return;
		}

		setUploading(true);
		try {
			const channel = isGemini ? channels.UPLOAD_TO_STORE : channels.UPLOAD_TO_OPENAI_STORE;
			const params = isGemini
				? { instanceId, storeName: selectedStore.name }
				: { instanceId, vectorStoreId: selectedStore.id };

			const result = await ipcRenderer.invoke(channel, params);

			if (result.success) {
				toast.success('Arquivo enviado com sucesso!');
				await loadDocuments(selectedStore.name);

				if (onChange) {
					onChange();
				}
			} else {
				if (result.error !== 'No file selected' && result.error !== 'File selection cancelled') {
					toast.error(`Erro ao enviar arquivo: ${result.error}`);
				}
			}
		} catch (error) {
			console.error('Error uploading file:', error);
			toast.error('Erro ao enviar arquivo');
		} finally {
			setUploading(false);
		}
	};

	const handleDeleteDocument = async (documentName) => {
		if (!window.confirm('Tem certeza que deseja deletar este arquivo?')) {
			return;
		}

		setLoading(true);
		try {
			const channel = isGemini ? channels.DELETE_DOCUMENT : channels.DELETE_OPENAI_FILE;
			const params = isGemini
				? { instanceId, documentName }
				: { instanceId, vectorStoreId: selectedStore.id, fileId: documentName };

			const result = await ipcRenderer.invoke(channel, params);

			if (result.success) {
				toast.success('Arquivo deletado!');
				await loadDocuments(selectedStore.name);

				if (onChange) {
					onChange();
				}
			} else {
				toast.error(`Erro ao deletar: ${result.error}`);
			}
		} catch (error) {
			console.error('Error deleting document:', error);
			toast.error('Erro ao deletar arquivo');
		} finally {
			setLoading(false);
		}
	};

	const handleActivateStore = async () => {
		if (!selectedStore) {
			toast.error('Selecione um store primeiro!');
			return;
		}

		try {
			const result = await ipcRenderer.invoke(channels.SET_ACTIVE_STORE, {
				instanceId,
				storeName: selectedStore.name
			});

			if (result.success) {
				setActiveStoreName(selectedStore.name);
				toast.success('Store ativado para IA!');

				if (onChange) {
					onChange();
				}
			} else {
				toast.error(`Erro ao ativar store: ${result.error}`);
			}
		} catch (error) {
			console.error('Error activating store:', error);
			toast.error('Erro ao ativar store');
		}
	};

	return (
		<>
			<Dialog open={isOpen} onOpenChange={onClose} modal={true}>
				<DialogPortal>
					<DialogOverlay className="z-[10000] bg-black/90 backdrop-blur-lg" />
					<DialogContent
						className="z-[10001] max-w-[900px] max-h-[90vh] overflow-y-auto bg-gradient-to-br from-modalBg via-modalBgSecondary to-modalBg backdrop-blur-3xl border border-modalBorder shadow-2xl"
						onPointerDownOutside={(e) => e.preventDefault()}
					>
						<button
							onClick={onClose}
							className="absolute right-4 top-4 z-50 flex items-center justify-center w-8 h-8 rounded-full bg-modalOverlayMedium backdrop-blur-xl border border-modalOverlayStrong text-textOnDarkMedium hover:text-white hover:bg-modalOverlayStrong transition-all duration-300 hover:scale-110"
						>
							<X className="w-4 h-4" />
						</button>

						<DialogHeader>
							<DialogTitle className="flex items-center gap-4 text-white">
								<div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-gray-800/80 via-statusInfo/30 to-purplePrimary/40 shadow-lg border border-white/20">
									<Database className="w-6 h-6 text-statusInfo drop-shadow-sm" />
								</div>
								<span className="text-xl font-semibold">
									Gerenciar Base de Conhecimento ({isGPT ? 'OpenAI' : 'Gemini'})
								</span>
							</DialogTitle>
						</DialogHeader>

						<div className="space-y-6">
							<div className="rounded-lg border border-statusInfo/25 bg-modalOverlaySoft backdrop-blur-xl p-4">
								<div className="flex items-start gap-3">
									<AlertCircle className="w-5 h-5 text-statusInfo flex-shrink-0 mt-0.5" />
									<div className="text-sm text-gray-300 space-y-2">
										<p>Gerencie múltiplos stores de documentos. Cada store pode conter arquivos diferentes.</p>
										<div className="text-xs text-gray-400">
											<p>Formatos suportados: {SUPPORTED_EXTENSIONS.join(', ')}</p>
											<p>Limite por arquivo: {MAX_FILE_SIZE_MB}MB | Limite: {MAX_STORES} stores por projeto</p>
										</div>
									</div>
								</div>
							</div>

							{activeStoreName && (
								<div className="rounded-lg border border-blue-500/30 bg-blue-500/10 backdrop-blur-xl p-4">
									<div className="flex items-start gap-3">
										<Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
										<div className="text-sm text-blue-300">
											Enquanto a Base de Conhecimento estiver ativa, as funcionalidades de
											Envio de Áudio por IA, Mídias por IA e Funções do Google Calendar
											serão temporariamente desabilitadas devido a uma limitação da API do Gemini.
										</div>
									</div>
								</div>
							)}

								<div className="rounded-lg border border-purple-500/25 bg-modalOverlaySoft backdrop-blur-xl p-4">
								<div className="flex items-center justify-between mb-3">
									<h3 className="text-sm font-semibold text-white flex items-center gap-2">
										<Database className="w-4 h-4 text-purple-400" />
										Stores Disponíveis ({stores.length}/{MAX_STORES})
										<div className="group relative">
											<Info className="w-3.5 h-3.5 text-gray-400 cursor-help hover:text-white transition-colors" />
											<div className="absolute bottom-full left-1/2 mb-2 hidden w-64 -translate-x-1/2 rounded-md bg-gray-800 px-3 py-2 text-xs text-white group-hover:block transition-all duration-200 z-50 shadow-lg border border-gray-700">
												Stores são coleções separadas de documentos. Você pode ter vários stores, mas apenas um pode estar ativo por vez na IA.
												<div className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-gray-800 border-r border-b border-gray-700"></div>
											</div>
										</div>
									</h3>
									<div className="flex gap-2">
										<button
											onClick={loadStores}
											disabled={loading}
											className="p-1.5 rounded hover:bg-gray-500/20 text-gray-400 transition-all disabled:opacity-50"
											title="Atualizar lista de stores"
										>
											<RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
										</button>
										<button
											onClick={() => setShowCreateDialog(true)}
											disabled={loading || stores.length >= MAX_STORES}
											className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 text-purple-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
										>
											<Plus className="w-3.5 h-3.5" />
											Criar Store
										</button>
									</div>
								</div>

								{loading ? (
									<div className="text-center py-4 text-gray-400">Carregando...</div>
								) : stores.length === 0 ? (
									<div className="text-center py-8 text-gray-400">
										<Database className="w-10 h-10 mx-auto mb-2 opacity-50" />
										<p className="text-sm">Nenhum store criado</p>
										<p className="text-xs mt-1">Clique em "Criar Store" para começar</p>
									</div>
								) : (
									<div className="space-y-2">
										{stores.map((store) => (
											<div
												key={store.name}
												className={`p-3 rounded-lg border transition-all cursor-pointer ${
													selectedStore?.name === store.name
														? 'bg-purple-500/20 border-purple-500/50'
														: 'bg-modalOverlayMedium border-modalOverlayStrong hover:border-purple-500/30'
												}`}
												onClick={() => handleSelectStore(store)}
											>
												<div className="flex items-center justify-between">
													<div className="flex items-center gap-3 flex-1">
														<div className="relative inline-flex items-center pointer-events-none">
															<input
																type="checkbox"
																checked={selectedStore?.name === store.name}
																onChange={() => {}}
																className="sr-only peer"
																tabIndex={-1}
															/>
															<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-500/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
														</div>
														<div className="flex-1 min-w-0">
															<p className="text-sm font-medium text-white truncate">
																{store.displayName}
															</p>
															<p className="text-xs text-gray-400">
																{store.documentCount || 0} arquivo(s)
																{activeStoreName === store.name && (
																	<span className="ml-2 text-green-400">• Ativo na IA</span>
																)}
															</p>
														</div>
													</div>
													<button
														onClick={(e) => {
															e.stopPropagation();
															handleDeleteStoreClick(store);
														}}
														disabled={loading}
														className="p-1.5 rounded hover:bg-red-500/20 text-red-400 transition-all disabled:opacity-50"
													>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											</div>
										))}
									</div>
								)}
								</div>

							{selectedStore && (
								<div className="rounded-lg border border-emeraldBase/25 bg-modalOverlaySoft backdrop-blur-xl p-4">
									<div className="flex items-center justify-between mb-3">
										<h3 className="text-sm font-semibold text-white flex items-center gap-2">
											<FileText className="w-4 h-4 text-emeraldMedium" />
											Arquivos em "{selectedStore.displayName}" ({documents.length})
										</h3>
										<div className="flex gap-2">
											<button
												onClick={() => loadDocuments(selectedStore.name)}
												disabled={loading}
												className="p-1.5 rounded hover:bg-gray-500/20 text-gray-400 transition-all disabled:opacity-50"
												title="Atualizar lista de arquivos"
											>
												<RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
											</button>
											<button
												onClick={handleUploadFile}
												disabled={uploading || loading}
												className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emeraldDark/80 hover:bg-emeraldMedium/80 border border-emeraldMedium/60 text-white transition-all disabled:opacity-50 text-sm"
											>
												<Upload className="w-3.5 h-3.5" />
												{uploading ? 'Enviando...' : 'Adicionar'}
											</button>
										</div>
									</div>

									<div className="max-h-[300px] overflow-y-auto space-y-2">
										{loading ? (
											<div className="text-center py-4 text-gray-400">Carregando...</div>
										) : documents.length === 0 ? (
											<div className="text-center py-8 text-gray-400">
												<FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
												<p className="text-sm">Nenhum arquivo neste store</p>
											</div>
										) : (
											documents.map((doc) => (
												<div
													key={doc.name}
													className="flex items-center justify-between p-3 rounded-lg bg-modalOverlayMedium border border-modalOverlayStrong hover:border-emeraldBase/30 transition-all"
												>
													<div className="flex items-center gap-3 flex-1 min-w-0">
														{getFileIcon(doc.mimeType)}
														<div className="flex-1 min-w-0">
															<p className="text-sm font-medium text-white truncate">
																{doc.displayName}
															</p>
															<div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
																<span>{formatFileSize(doc.sizeBytes || 0)}</span>
																<span>•</span>
																<span className={`flex items-center gap-1 ${getStateColor(doc.state)}`}>
																	{getStateIcon(doc.state)}
																	{doc.state}
																</span>
																{doc.createTime && (
																	<>
																		<span>•</span>
																		<span>{formatDate(doc.createTime)}</span>
																	</>
																)}
															</div>
														</div>
													</div>
													<button
														onClick={() => handleDeleteDocument(doc.name)}
														disabled={loading}
														className="p-1.5 rounded hover:bg-red-500/20 text-red-400 transition-all disabled:opacity-50"
													>
														<Trash2 className="w-4 h-4" />
													</button>
												</div>
											))
										)}
									</div>
								</div>
							)}

							{selectedStore && (
								<div className="rounded-lg border border-green-500/25 bg-modalOverlaySoft backdrop-blur-xl p-4">
									<div className="flex items-center justify-between">
										<div>
											<h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
												Store Ativo para IA
												<div className="group relative">
													<Info className="w-3.5 h-3.5 text-gray-400 cursor-help hover:text-white transition-colors" />
													<div className="absolute bottom-full left-1/2 mb-2 hidden w-64 -translate-x-1/2 rounded-md bg-gray-800 px-3 py-2 text-xs text-white group-hover:block transition-all duration-200 z-50 shadow-lg border border-gray-700">
														O store ativo é aquele que a IA usará para buscar informações e responder suas perguntas.
														<div className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-gray-800 border-r border-b border-gray-700"></div>
													</div>
												</div>
											</h3>
											<p className="text-xs text-gray-400">
												{activeStoreName === selectedStore.name
													? `✓ "${selectedStore.displayName}" está ativo`
													: activeStoreName
													? 'Outro store está ativo (clique em "Usar na IA" para ativar este)'
													: 'Nenhum store ativo (clique em "Usar na IA" para ativar)'}
											</p>
										</div>
										<div className="px-3 py-1.5 rounded-lg bg-green-500/20 border border-green-500/30 text-green-300 text-sm flex items-center gap-2">
											<Check className="w-4 h-4" />
											Store Ativo
										</div>
									</div>
								</div>
							)}
						</div>

						<div className="flex justify-end pt-4 border-t border-modalBorder">
							<button
								onClick={onClose}
								className="px-4 py-2 rounded-lg bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600 text-white transition-all"
							>
								Fechar
							</button>
						</div>
					</DialogContent>
				</DialogPortal>
			</Dialog>

			<Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
				<DialogPortal>
					<DialogOverlay className="z-[10002]" />
					<DialogContent className="z-[10003] max-w-md">
						<DialogHeader>
							<DialogTitle>Criar Novo Store</DialogTitle>
						</DialogHeader>
						<div className="space-y-4">
							<div>
								<label className="text-sm font-medium text-gray-300 mb-2 block">
									Nome do Store
								</label>
								<input
									type="text"
									value={newStoreName}
									onChange={(e) => setNewStoreName(e.target.value)}
									placeholder="Ex: meus-produtos"
									className="w-full px-3 py-2 rounded-lg bg-modalOverlaySoft border border-modalBorder text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
									onKeyDown={(e) => e.key === 'Enter' && handleCreateStore()}
								/>
								<p className="text-xs text-gray-400 mt-1">
									Limite: {stores.length}/{MAX_STORES} stores
								</p>
							</div>
							<div className="flex justify-end gap-2">
								<button
									onClick={() => {
										setShowCreateDialog(false);
										setNewStoreName('');
									}}
									className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-500 text-white transition-all"
								>
									Cancelar
								</button>
								<button
									onClick={handleCreateStore}
									disabled={loading || !newStoreName.trim()}
									className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
								>
									Criar
								</button>
							</div>
						</div>
					</DialogContent>
				</DialogPortal>
			</Dialog>

			<Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
				<DialogPortal>
					<DialogOverlay className="z-[10002]" />
					<DialogContent className="z-[10003] max-w-md border border-red-500/30">
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2 text-red-400">
								<AlertCircle className="w-5 h-5" />
								ATENÇÃO: AÇÃO IRREVERSÍVEL
							</DialogTitle>
						</DialogHeader>
						<div className="space-y-4">
							<div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
								<p className="text-sm text-gray-300 mb-2">
									Deletar o store <strong className="text-white">"{storeToDelete?.displayName}"</strong>?
								</p>
								<div className="text-sm text-red-400 space-y-1">
									<p>⚠️ {storeToDelete?.documentCount || 0} arquivo(s) serão perdidos permanentemente!</p>
									<p>⚠️ Esta ação NÃO pode ser desfeita!</p>
								</div>
							</div>
							<div>
								<label className="text-sm font-medium text-gray-300 mb-2 block">
									Digite o nome do store para confirmar:
								</label>
								<input
									type="text"
									value={deleteConfirmName}
									onChange={(e) => setDeleteConfirmName(e.target.value)}
									placeholder={storeToDelete?.displayName || ''}
									className="w-full px-3 py-2 rounded-lg bg-modalOverlaySoft border border-red-500/30 text-white placeholder-gray-400 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
								/>
							</div>
							<div className="flex justify-end gap-2">
								<button
									onClick={() => {
										setShowDeleteConfirm(false);
										setStoreToDelete(null);
										setDeleteConfirmName('');
									}}
									className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-500 text-white transition-all"
								>
									Cancelar
								</button>
								<button
									onClick={handleConfirmDelete}
									disabled={loading || deleteConfirmName !== storeToDelete?.displayName}
									className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
								>
									🗑️ DELETAR
								</button>
							</div>
						</div>
					</DialogContent>
				</DialogPortal>
			</Dialog>
		</>
	);
};

export default KnowledgeBaseSection;
